import { getLogger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import type { AgentActivity, ChatTask } from "@/sdk";
import type { AsyncState } from "@/shared-types/utility";
import { TwilioTask, type TwilioTaskConfig, type TwilioTaskProps } from "../twilioTask";
import type { TwilioChatTaskAttributes } from "../twilioTaskAttributes";

const logger = getLogger({
    module: "TwilioChatTask",
});

function getTaskPartner(config: TwilioTaskConfig) {
    const partner = config.reservation.task.attributes.partner as Partner;
    return partner.toLowerCase();
}

export class TwilioChatTask extends TwilioTask<TwilioChatTaskAttributes> implements ChatTask {
    public override readonly onAcceptStatus = "Available" as AgentActivity;
    public override readonly hasCustomer = true;
    public override readonly sessionId = undefined;
    public override readonly partner: Partner;
    public override readonly autoAccept = false;
    public override readonly wrappingDuration: number = 20 * 1000;

    constructor(name: string, props: TwilioTaskProps, agentPartner: Partner) {
        super(name, props);
        this.partner = getTaskPartner(this.config) ?? agentPartner;
        this.mdn = { loading: false };
        this.problemStatement = { loading: false };
        this.customerName = { loading: false };
    }

    get chatId(): string {
        return this.attributes.conversationSid;
    }

    public mdn: AsyncState<string | undefined>;
    public problemStatement: AsyncState<string | undefined>;
    public customerName: AsyncState<string | undefined>;
    public isCustomerAbsent: boolean = false;
    public customerAbsenceStartedAt: Date | undefined;
    public customerAbsenceEndsAt: Date | undefined;

    public get isNeglected(): boolean {
        return !!this.attributes.neglectionWarningCount;
    }

    public get wasTaskNeglected(): boolean {
        return this.attributes?.transferReason === "Neglection";
    }

    public get isClosedByCustomer(): boolean {
        return this.statusReason === "ClosedByCustomer";
    }

    public get wasCustomerDisconnected(): boolean {
        return this.statusReason === "CustomerDisconnected";
    }

    public get changeStateAt(): Date | undefined {
        if (this.status === "wrapping") {
            const wrappingStart = this.statusUpdatedAt?.getTime() ?? this.config.reservation.task.dateUpdated.getTime();
            return new Date(wrappingStart + this.wrappingDuration);
        }

        if (this.isNeglected && this.neglectionWarningDurationMillis) {
            return new Date(Date.now() + this.neglectionWarningDurationMillis);
        }

        if (this.isCustomerAbsent && this.customerAbsenceEndsAt) {
            return this.customerAbsenceEndsAt;
        }

        return undefined;
    }

    public get stateLastChangedAt(): Date | undefined {
        if (this.status === "wrapping") {
            return this.statusUpdatedAt ?? this.config.reservation.task.dateUpdated;
        }

        if (this.isNeglected && this.attributes.neglectionWarningStartedAt) {
            return new Date(this.attributes.neglectionWarningStartedAt);
        }

        if (this.isCustomerAbsent && this.customerAbsenceStartedAt) {
            return this.customerAbsenceStartedAt;
        }

        return undefined;
    }

    public override get previousTaskId(): string | undefined {
        throw new Error("previousTaskID is not supported for Twilio Chat");
    }

    public override onAccepted(): void | Promise<void> {} // not used

    public override isAgentBusy(_agentActivity: AgentActivity): boolean {
        return ["assigned", "wrapping", "pending"].includes(this.status);
    }

    /**
     * Get the neglection warning duration in milliseconds.
     * If the neglection warning is not active, returns undefined.
     * If the neglection warning is active but the warning count is not 1, returns 0.
     * If the neglection warning is active and the warning count is 1, returns the remaining time in milliseconds.
     * This is computed by subtracting the time passed since the warning started in the backend, from the total duration.
     *
     * The duration received from the server is the total duration before the task gets transferred.
     * We receive neglection warning count multiple times (1, 2, 3), but we only need to start showing the warning
     * when warning count is 1.
     *
     * @returns The remaining time in milliseconds for the neglection warning to be displayed.
     **/
    get neglectionWarningDurationMillis(): number | undefined {
        if (!this.isNeglected) return undefined;
        if (
            this.attributes.neglectionWarningCount !== 1 ||
            !this.attributes.neglectionWarningStartedAt ||
            !this.attributes.neglectionWarningDurationSeconds
        ) {
            return 0;
        }
        const neglectionWarningDurationMillis = this.attributes.neglectionWarningDurationSeconds * 1000;
        const now = Date.now();
        const serverStart = Date.parse(this.attributes.neglectionWarningStartedAt);
        if (Number.isNaN(serverStart)) {
            return neglectionWarningDurationMillis;
        } else {
            const duration = neglectionWarningDurationMillis - (now - serverStart);
            return Math.max(0, duration);
        }
    }

    public override get analyticsData(): Record<string, string> {
        return {
            ...super.analyticsData,
            ChatId: this.chatId,
        };
    }

    public get routingQueue(): string | undefined {
        return this.config.reservation.task.queueName;
    }

    public get sourceApp(): string | undefined {
        const sourceApp = this.attributes.sourceApp;
        if (!sourceApp) {
            logger.error(
                {
                    chatId: this.chatId,
                    taskSid: this.config.reservation.task.sid,
                },
                "CRITICAL: sourceApp is missing from chat task attributes",
            );
        }
        return sourceApp;
    }

    public get workerSid(): string {
        return this.worker.sid;
    }

    public get topic(): string | undefined {
        const topicFromAttributes = this.config.reservation.task.attributes.topic as string | undefined;

        if (!topicFromAttributes) {
            logger.error(
                {
                    chatId: this.chatId,
                    taskSid: this.config.reservation.task.sid,
                },
                "CRITICAL: Topic is missing from task attributes - sales features will not work",
            );
        }

        return topicFromAttributes;
    }

    public get language(): string | undefined {
        const languageFromAttributes = this.config.reservation.task.attributes.language as string | undefined;

        if (!languageFromAttributes) {
            logger.error(
                {
                    chatId: this.chatId,
                    taskSid: this.config.reservation.task.sid,
                },
                "CRITICAL: Language is missing from task attributes - sales features will not work",
            );
        }

        return languageFromAttributes;
    }

    public get neglectionCount(): number | undefined {
        return this.attributes.ignoreExpertIds?.length;
    }

    override toLog(): Record<string, unknown> {
        return {
            ...super.toLog(),
            chatId: this.chatId,
        };
    }
}
